import { Platform, Text, View } from "@-/com.npmjs.react-native";
import type { MorphemeDetailsTileStatsSectionGate } from "#get-morpheme-details-tile-stats-section-gate";
import { styled } from "styled-components/native";
import { SURFACE_STATS_SPACING_CLASS_NAME } from "@-/dev.improve.haniverse.components.ui.surface";

// Stats labels (#332 / TBCL 1) scale with the enclosing card's inline-size. On
// web we still use `cqi` so the labels track the card's container width; on
// native we compute the font size from the card's pixel `size` prop because the
// `clamp(.., 5cqi, ..)` declaration drops silently and collapses the labels to
// zero height (verified via inspect-element on iPhone 16e — same root cause as
// the CardDetailsTileUi edge-label fix).
const STATS_RATIO = 0.05;
const STATS_MIN_PX = 9;
const STATS_MAX_PX = 18;
const STATS_LINE_HEIGHT_RATIO = 1.15;

function getStatsFontSize(size: number): number {
  return Math.max(STATS_MIN_PX, Math.min(STATS_MAX_PX, size * STATS_RATIO));
}

// `line-height` is set in pixels rather than the unitless `1.15` the web build
// uses because React Native interprets unitless line-height as literal pixels
// rather than the CSS multiplier, which would crush the text to ~1px tall.
const WebStatsLabel = styled.Text`
  font-size: clamp(9px, 5cqi, 18px);
  line-height: 1.15;
  color: hsl(var(--color-muted-foreground));
`;

export function MorphemeDetailsTileStatsSection({
  gate,
  size,
}: {
  gate: MorphemeDetailsTileStatsSectionGate;
  size: number;
}) {
  const { levelLabel, rankLabel } = gate;

  if (Platform.OS === "web") {
    return (
      <View
        className={`min-w-0 shrink-0 flex-row items-end gap-1 self-stretch ${SURFACE_STATS_SPACING_CLASS_NAME}`}
      >
        {levelLabel !== null ? <WebStatsLabel numberOfLines={1}>{levelLabel}</WebStatsLabel> : null}
        <View className="flex-1" />
        {rankLabel !== null ? <WebStatsLabel numberOfLines={1}>{rankLabel}</WebStatsLabel> : null}
      </View>
    );
  }

  const fontSize = getStatsFontSize(size);
  return (
    <View
      className={`min-w-0 shrink-0 flex-row items-end gap-1 self-stretch ${SURFACE_STATS_SPACING_CLASS_NAME}`}
    >
      {levelLabel !== null ? (
        <Text
          className="text-muted-foreground"
          numberOfLines={1}
          style={{ fontSize, lineHeight: fontSize * STATS_LINE_HEIGHT_RATIO }}
        >
          {levelLabel}
        </Text>
      ) : null}
      <View className="flex-1" />
      {rankLabel !== null ? (
        <Text
          className="text-muted-foreground"
          numberOfLines={1}
          style={{ fontSize, lineHeight: fontSize * STATS_LINE_HEIGHT_RATIO }}
        >
          {rankLabel}
        </Text>
      ) : null}
    </View>
  );
}
